import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Webhook, X, Eye, EyeOff, Copy, CheckCheck, Settings2 } from 'lucide-react';
import type { IntegrationConfig } from '../../../types/adminConsole';
import {
  INTEGRATION_SETUP,
  WEBHOOK_EVENT_OPTIONS,
  initialIntegrationEndpointUrl,
  initialWebhookEndpointUrl,
  type IntegrationKey,
} from '../integrations/integrationCatalog';

interface ModalShellProps {
  title: string;
  icon: React.ReactNode;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}

const ModalShell: React.FC<ModalShellProps> = ({ title, icon, subtitle, onClose, children, footer }) =>
  createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="enterprise-card-surface flex w-full max-w-lg max-h-[90vh] flex-col overflow-hidden shadow-xl"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-5 py-4">
          <div className="flex min-w-0 items-start gap-2.5">
            <span className="mt-0.5 text-[var(--brand-primary)]">{icon}</span>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h2>
              {subtitle && <p className="mt-1 text-xs text-[var(--text-secondary)]">{subtitle}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded p-1 hover:bg-[var(--button-hover)]" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        <div className="flex justify-end gap-2 border-t border-[var(--border-default)] px-5 py-4">{footer}</div>
      </div>
    </div>,
    document.body,
  );

export interface WebhookModalSavePayload {
  webhook_url: string;
  webhook_secret: string;
  events: string[];
}

interface WebhookConfigModalProps {
  integration: IntegrationConfig;
  onSave: (payload: WebhookModalSavePayload) => Promise<void>;
  onClose: () => void;
}

export const WebhookConfigModal: React.FC<WebhookConfigModalProps> = ({ integration, onSave, onClose }) => {
  const [url, setUrl] = useState(() => initialWebhookEndpointUrl(integration.webhook_url));
  const [secret, setSecret] = useState(integration.webhook_secret ?? '');
  const [events, setEvents] = useState<string[]>(integration.events ?? []);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setUrl(initialWebhookEndpointUrl(integration.webhook_url));
    setSecret(integration.webhook_secret ?? '');
    setEvents(integration.events ?? []);
  }, [integration.webhook_url, integration.webhook_secret, integration.events]);

  const toggleEvent = (key: string) =>
    setEvents((prev) => (prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]));

  const generateSecret = () => {
    const arr = new Uint8Array(24);
    crypto.getRandomValues(arr);
    setSecret(Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join(''));
  };

  const copySecret = async () => {
    if (!secret) return;
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!url.trim()) return;
    setSaving(true);
    try {
      await onSave({ webhook_url: url.trim(), webhook_secret: secret.trim(), events });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="Webhooks — Endpoint e gatilhos"
      icon={<Webhook size={18} />}
      subtitle="Envie eventos operacionais para Make, Zapier, n8n ou qualquer URL HTTPS."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="enterprise-button-secondary px-3 py-1.5 text-xs" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="enterprise-btn-primary px-3 py-1.5 text-xs disabled:opacity-50"
            disabled={saving || !url.trim()}
            onClick={handleSave}
          >
            {saving ? 'Salvando...' : 'Salvar e conectar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            URL do endpoint *
          </label>
          <input
            type="text"
            inputMode="url"
            name="wf-webhook-endpoint"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://seu-endpoint.com/webhook"
            className="enterprise-input w-full text-sm"
          />
          <p className="mt-1 text-[10px] text-[var(--text-muted)]">
            O sistema enviará POST JSON assinado (header <code className="font-mono">X-WF-Signature</code>).
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Secret HMAC (opcional)
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type={showSecret ? 'text' : 'password'}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Chave para validar assinatura"
                className="enterprise-input w-full pr-8 text-sm"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                onClick={() => setShowSecret((v) => !v)}
              >
                {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
            </div>
            <button type="button" className="enterprise-button-secondary shrink-0 px-2.5 text-[11px]" onClick={generateSecret}>
              Gerar
            </button>
            <button type="button" className="enterprise-button-secondary shrink-0 px-2.5 text-[11px]" onClick={copySecret}>
              {copied ? <CheckCheck size={13} className="text-emerald-500" /> : <Copy size={13} />}
            </button>
          </div>
        </div>

        <div>
          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Gatilhos
          </label>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {WEBHOOK_EVENT_OPTIONS.map((ev) => (
              <label
                key={ev.key}
                className="flex cursor-pointer items-center gap-2 rounded border border-[var(--border-default)] bg-[var(--bg-elevated)]/50 px-2.5 py-2 hover:bg-[var(--button-hover)]"
              >
                <input
                  type="checkbox"
                  checked={events.includes(ev.key)}
                  onChange={() => toggleEvent(ev.key)}
                  className="accent-[var(--brand-primary)]"
                />
                <span className="text-[11px] text-[var(--text-primary)]">{ev.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export interface ToolIntegrationSavePayload {
  webhook_url: string;
  webhook_secret: string;
  notes: string;
}

interface ToolIntegrationModalProps {
  integrationKey: Exclude<IntegrationKey, 'webhooks'>;
  integration: IntegrationConfig;
  onSave: (payload: ToolIntegrationSavePayload) => Promise<void>;
  onClose: () => void;
}

export const ToolIntegrationModal: React.FC<ToolIntegrationModalProps> = ({
  integrationKey,
  integration,
  onSave,
  onClose,
}) => {
  const meta = INTEGRATION_SETUP[integrationKey];
  const [url, setUrl] = useState(() => initialIntegrationEndpointUrl(integration.webhook_url));
  const [secret, setSecret] = useState(integration.webhook_secret ?? '');
  const [notes, setNotes] = useState(
    typeof integration.config_json?.notes === 'string' ? integration.config_json.notes : '',
  );
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setUrl(initialIntegrationEndpointUrl(integration.webhook_url));
    setSecret(integration.webhook_secret ?? '');
    setNotes(typeof integration.config_json?.notes === 'string' ? integration.config_json.notes : '');
  }, [integration.webhook_url, integration.webhook_secret, integration.config_json]);

  const handleSave = async () => {
    if (!url.trim() && !secret.trim()) return;
    setSaving(true);
    try {
      await onSave({ webhook_url: url.trim(), webhook_secret: secret.trim(), notes: notes.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={meta.title}
      icon={<Settings2 size={18} />}
      subtitle={meta.intro}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="enterprise-button-secondary px-3 py-1.5 text-xs" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="enterprise-btn-primary px-3 py-1.5 text-xs disabled:opacity-50"
            disabled={saving || (!url.trim() && !secret.trim())}
            onClick={handleSave}
          >
            {saving ? 'Salvando...' : 'Salvar e conectar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <ol className="list-decimal space-y-1 pl-4 text-[11px] text-[var(--text-secondary)]">
          {meta.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            {meta.urlLabel}
          </label>
          <input
            type="text"
            inputMode="url"
            name={`wf-integration-endpoint-${integrationKey}`}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={meta.urlPlaceholder}
            className="enterprise-input w-full text-sm"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            {meta.secretLabel}
          </label>
          <div className="relative">
            <input
              type={showSecret ? 'text' : 'password'}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={meta.secretPlaceholder}
              className="enterprise-input w-full pr-8 text-sm"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
              onClick={() => setShowSecret((v) => !v)}
            >
              {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Notas (opcional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Ambiente, responsável, observações..."
            className="enterprise-input w-full resize-none text-sm"
          />
        </div>

        <p className="rounded border border-blue-200/60 bg-blue-50/50 px-3 py-2 text-[10px] text-blue-800 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200">
          Esta fase persiste credenciais para validação de tela e simulação de conexão. Não dispara sync real com o ERP.
        </p>
      </div>
    </ModalShell>
  );
};
